import { useCallback, useState } from "react";
import { fieldErrors } from "@/api/client";

/** Controlled form values with a typed per-field setter. */
export function useFormValues<T extends object>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  }, []);
  return { values, set, setValues };
}

/** Saving flag plus the last error, with 422 field messages already mapped. */
export function useSubmitState() {
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  const run = useCallback(async (action: () => Promise<unknown>): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { error, saving, run, errors: fieldErrors(error) };
}
